import React, { useEffect, useMemo, useState } from "react";
import { Cloud } from "lucide-react";

import { Header } from "../layout/Header";
import { CategorySidebar, MobileCategorySidebar } from "../layout/CategorySidebar";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { AuthorCloud, CloudAuthor } from "./AuthorCloud";
import { ShareLinkButton } from "../layout/ShareLinkButton";
import { TopFiveButton, TOP_COUNT } from "../layout/TopFiveButton";
import { useAppHeaderProps } from "../../lib/useAppHeaderProps";
import { getStoredCurrentUser } from "../../lib/app-events";
import ApiService from "../../services/api-service";
import { CurrentUser } from "../../types/user";
import { categoryNameFromId } from "../../lib/categories";
import { useUrlCategory, useUrlParam } from "../../lib/url-state";
import { slugify } from "../../lib/slug";

type QuotePost = { type: string; author?: string | null };

// Builds the author cloud from a category's posts the same way GET
// /quotes/authors does: quotes only, spellings that share a slug are merged
// (counts summed) and shown under the most-used spelling.
function authorCloudFromPosts(posts: QuotePost[]): CloudAuthor[] {
  const countByName = new Map<string, number>();
  for (const post of posts) {
    const name = post.type === "quote" ? String(post.author || "").trim() : "";
    if (name) countByName.set(name, (countByName.get(name) ?? 0) + 1);
  }

  const bySlug = new Map<string, CloudAuthor & { topSpellingCount: number }>();
  for (const [name, count] of countByName) {
    const slug = slugify(name);
    if (!slug) continue;
    const entry = bySlug.get(slug);
    if (!entry) {
      bySlug.set(slug, { name, slug, count, topSpellingCount: count });
    } else {
      entry.count += count;
      if (count > entry.topSpellingCount) {
        entry.name = name;
        entry.topSpellingCount = count;
      }
    }
  }
  return [...bySlug.values()]
    .map(({ name, slug, count }) => ({ name, slug, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

// /quotes/authors - every quote author as a word cloud (bigger = more quotes),
// with the category sidebar alongside. Picking a category (?category=)
// narrows the cloud to authors quoted in that category instead of opening its
// feed, and "Top 5 Authors" (?top=5) to its five most-quoted.
export function AuthorCloudPage() {
  const { onOpenAuth, onOpenCreateModal } = useAppHeaderProps();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(getStoredCurrentUser);
  const [authors, setAuthors] = useState<CloudAuthor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [activeCategory, setActiveCategory, getCategoryHref] = useUrlCategory();
  const categoryName = activeCategory === "all" ? null : categoryNameFromId(activeCategory);
  const [topParam, setTopParam] = useUrlParam<"all" | "5">("top", "all", ["all", "5"]);
  const showTopOnly = topParam === "5";

  useEffect(() => {
    const handleUserChange = (event: Event) => {
      setCurrentUser(event instanceof CustomEvent ? event.detail ?? null : getStoredCurrentUser());
    };
    window.addEventListener("inspirelens:userchange", handleUserChange);
    return () => window.removeEventListener("inspirelens:userchange", handleUserChange);
  }, []);

  useEffect(() => {
    document.title = "Author Cloud · The Deepam";
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    // "All" uses the server's list; a single category is built from that
    // category's posts, which already carry each quote's author.
    const request: Promise<CloudAuthor[]> =
      activeCategory === "all"
        ? ApiService.getQuoteAuthors().then((res: { authors?: CloudAuthor[] }) => (Array.isArray(res?.authors) ? res.authors : []))
        : ApiService.getAllContentInCategory(activeCategory).then((res: { posts?: QuotePost[] }) =>
            authorCloudFromPosts(Array.isArray(res?.posts) ? res.posts : [])
          );
    request
      .then((list) => {
        if (!cancelled) setAuthors(list);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [retryToken, activeCategory]);

  // authors is already sorted most-quoted first, so the top five are its head.
  const shownAuthors = useMemo(() => (showTopOnly ? authors.slice(0, TOP_COUNT) : authors), [authors, showTopOnly]);
  const totalQuotes = useMemo(() => shownAuthors.reduce((sum, author) => sum + author.count, 0), [shownAuthors]);
  const authorCountLabel = showTopOnly
    ? `Top ${shownAuthors.length} of ${authors.length} ${authors.length === 1 ? "author" : "authors"}`
    : `${authors.length} ${authors.length === 1 ? "author" : "authors"}`;

  return (
    <div className="max-w-[85rem] mx-auto px-3 py-6">
      <Header currentUser={currentUser} onOpenAuth={onOpenAuth} onOpenCreateModal={onOpenCreateModal} />

      <div className="mt-6 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Cloud className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400 shrink-0" aria-hidden="true" />
            Author Cloud
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            {loading
              ? `Gathering every author quoted ${categoryName ? `in ${categoryName}` : "on The Deepam"}...`
              : `${categoryName ? `${categoryName}: ` : ""}${authorCountLabel} · ${totalQuotes} ${totalQuotes === 1 ? "quote" : "quotes"}. Bigger names have more quotes - tap one to read them.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TopFiveButton noun="Authors" active={showTopOnly} onToggle={() => setTopParam(showTopOnly ? "all" : "5")} disabled={loading} />
          <MobileCategorySidebar activeCategory={activeCategory} onCategoryChange={setActiveCategory} getCategoryHref={getCategoryHref} isLoading={loading} label={categoryName ?? "Categories"} />
          <ShareLinkButton title="Author Cloud" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
        <aside className="hidden md:block md:col-span-1">
          <div className="sticky top-6">
            <CategorySidebar activeCategory={activeCategory} onCategoryChange={setActiveCategory} getCategoryHref={getCategoryHref} isLoading={loading} />
          </div>
        </aside>

        <main className="md:col-span-3 min-w-0">
          {loading ? (
            <Skeleton className="w-full h-[420px] sm:h-[520px] rounded-3xl" />
          ) : error ? (
            <div className="rounded-3xl border border-slate-200 dark:border-neutral-800 py-16 text-center space-y-3">
              <p className="text-slate-700 dark:text-slate-200 font-medium">Couldn't load authors.</p>
              <Button variant="outline" size="sm" onClick={() => setRetryToken((n) => n + 1)}>
                Try again
              </Button>
            </div>
          ) : authors.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 dark:border-neutral-800 py-16 text-center text-slate-600 dark:text-slate-400">
              {categoryName ? `No authors in ${categoryName} yet.` : "No quotes with an author yet."}
            </div>
          ) : (
            <AuthorCloud authors={shownAuthors} />
          )}
        </main>
      </div>
    </div>
  );
}
